<template>
  <DModal :show-close="false">
    <div class="d-modal d-modal__user__settings__notifications text-dark" slot="insertComponent">
      <div class="d-modal__header">
        <h2>{{ i18n[locale]['user_settings'] }}</h2>
        <div class="d-modal__close icon__bg-circle" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>
      <div class="d-modal__content">
        <h2>{{ user.name }}</h2>
        <div class="d-input-group__group" v-if="isSMSActive">
          <div class="d-input-group input-group">
            <label class="d-label">{{ i18n[locale]['mobile_number'] }}</label>
            <div class="flex-group" :class="{'animated shake': isAnimatePhone}">
              <div class="flag__input-lookalike active-country">
                <img :src="activeCountry.flag" class="country-flag"/>+{{ activeCountry.countryCode }}
                <DDropdown>
                  <span class="icon-arrow-down5" slot="d-dropdown__icon"></span>
                  <ul slot="list">
                    <li class="country-select__item" v-for="country in countries" :key="country.countryCode"
                        @click="setActiveCountry(country)">
                      <div class="country-select__item__name"><img :src="country.flag" class="country-flag"/>
                        <p>{{ country.name }}</p></div>
                      + {{ country.countryCode }}
                    </li>
                  </ul>
                </DDropdown>
              </div>
              <input class="d-input"
                     ref="input_phone"
                     :class="{'input--error': !isPhoneValid}"
                     v-model="phone"
                     @keyup="onPhoneKeyup($event.target.value)">
            </div>
            <span class="error-text marg-1-top" v-if="!isPhoneValid">{{ i18n[locale]['number_must_be_8_digits'] }}</span>
          </div>
          <div class="d-label d-pill" v-if="user.unconfirmed_phone"
               :class="{'animated shake': isAnimatePhoneUnconfirmed}">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                 stroke="currentColor" class="w-6 h-6">
              <path stroke-linecap="round" stroke-linejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
            </svg>
            {{ i18n[locale]['awaiting_approval'] }}
          </div>
          <div class="d-input-group marg-2-top"
               v-if="!hideSMSToggle && (user.v_phone_setting === 'active' || user.v_phone_setting === 'deactivated')">
            <label class="d-label">{{ i18n[locale]['receive_sms'] }}</label>
            <div class="d-input-group__toggle" @click="toggleSetting('phone')">
              <app-toggle :allowChanges="false" :propOn="receiveSMS"></app-toggle>
            </div>
          </div>
        </div>
        <div class="d-input-group__group" v-if="isEmailActive">
          <div class="d-input-group input-group">
            <label class="d-label">{{ i18n[locale]['email'] }}</label>
            <input class="d-input" style="height: 40px"
                   :class="{'input--error': !isEmailValid}"
                   v-model="email"
                   @keyup="onEmailKeyup($event.target.value)">
            <span class="error-text marg-1-top" v-if="!isEmailValid">{{ i18n[locale]['use_a_valid_email'] }}</span>
          </div>
          <div class="d-input-group marg-2-top"
               v-if="!hideEmailToggle && (user.v_email_setting === 'active' || user.v_email_setting === 'deactivated')">
            <label class="d-label">{{ i18n[locale]['receive_email'] }}</label>
            <div class="d-input-group__toggle" @click="receiveEmails = !receiveEmails">
              <app-toggle :allowChanges="false" :propOn="receiveEmails"></app-toggle>
            </div>
          </div>
        </div>
        <div class="d-input-group d-input-group__submit">
          <button @click="$emit('close')">{{ $t('vue2.common.cancel') }}</button>
          <button class="bttn bttn-primary"
                  :class="[{' bttn--loading': isSubmitting}, {'bttn-primary--disabled': !this.isEmailValid || !this.isPhoneValid}]"
                  @click="submit">
            {{ $t('vue2.common.save') }}
          </button>
        </div>
      </div>
    </div>
  </DModal>
</template>

<script>
import docendo from '../../docendo'
import DModal from '../common/DModal'
import AppToggle from '../common/AppToggle'
import DDropdown from '../common/DDropdown'
import SmsAddNumber from './SmsAddNumber'
import eventbus from '../../eventbus'

const I18N = {
  da: {
    awaiting_approval: 'Afventer bekræftelse',
    user_settings: 'Brugerindstillinger',
    mobile_number: 'Mobilnummer',
    number_must_be_8_digits: 'Telefonummer skal være 8 cifre',
    receive_sms: 'Modtag SMS',
    email: 'Email',
    use_a_valid_email: 'Skriv en gyldig email',
    receive_email: 'Modtag email',
    user_updated: 'Opdateret'
  },
  nb: {
    awaiting_approval: 'Venter på bekreftelse',
    user_settings: 'brukerinstillinger',
    mobile_number: 'Telefonnummer',
    number_must_be_8_digits: 'Telefonnummer må være 8 sifre',
    receive_sms: 'Motta SMS',
    email: 'E-post',
    use_a_valid_email: 'Bruk en gyldig e-post',
    receive_email: 'Motta epost',
    user_updated: 'Oppdatert'
  },
  en: {
    awaiting_approval: 'Awaiting approval',
    user_settings: 'User settings',
    mobile_number: 'Mobile number',
    number_must_be_8_digits: 'Mobile number must be 8 digits',
    receive_sms: 'Receive sms',
    email: 'Email',
    use_a_valid_email: 'Use a valid email',
    receive_email: 'Receive email',
    user_updated: 'User updated'
  }
}

export default {
  components: {AppToggle, DModal, DDropdown, SmsAddNumber},
  props: {
    user: {},
    isEmailActive: {
      type: Boolean,
      default: true
    },
    isSMSActive: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      phone: '',
      initialPhone: '',
      isPhoneValid: true,
      isAnimatePhoneUnconfirmed: false,
      isAnimatePhone: false,
      email: '',
      initialEmail: '',
      initialReceiveSMS: true,
      receiveSMS: true,
      hideSMSToggle: false,
      receiveEmails: true,
      initialReceiveEmails: true,
      isEmailValid: true,
      hideEmailToggle: false,
      activeCountry: null,
      countries: [
        {name: 'Danmark', countryCode: '45', code: 'dk', flag: 'https://zen.docendo.co/flags/dk.svg'},
        {name: 'Norge', countryCode: '47', code: 'no', flag: 'https://zen.docendo.co/flags/no.svg'},
        {name: 'United States', countryCode: '1', code: 'us', flag: 'https://zen.docendo.co/flags/us.svg'}
      ],
      isSubmitting: false,
      initialUserSettings: null,
      userSettings: null,
      errors: {}
    }
  },
  computed: {
    i18n () {
      return I18N
    },
    locale () {
      return window.USER.language ? window.USER.language : 'en'
    },
    isUSA () {
      return this.activeCountry.code === 'us'
    },
    isNumberBeingDeleted () {
      return this.phone.length === 0 && (this.phone !== this.initialPhone)
    }
  },
  methods: {
    toggleSetting (contactType) {
      if (contactType === 'phone') {
        if (this.user.v_phone_setting === 'no_number' || this.phone.length < 1) {
          this.isPhoneValid = false
          this.isAnimatePhone = true
          clearTimeout(dTimer)
          this.$refs.input_phone.focus()
          var dTimer = setTimeout(_ => {
            this.isAnimatePhone = false
          }, 1000)
          return
        }
        if (this.user.v_phone_setting === 'awaiting_approval') {
          let dTimer = null
          clearTimeout(dTimer)
          this.isAnimatePhoneUnconfirmed = true
          dTimer = setTimeout(_ => {
            this.isAnimatePhoneUnconfirmed = false
          }, 1000)
          return
        }
        this.receiveSMS = !this.receiveSMS
      }
    },
    validateEmail (email) {
      const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      this.isEmailValid = email.length < 1 ? true : re.test(String(email).toLowerCase())
    },
    setActiveCountry (country) {
      this.activeCountry = country
    },
    onPhoneKeyup (number) {
      if (number.length === 0 || number.length > 8) {
        this.hideSMSToggle = true
      }
      if (number.length === 8) {
        this.hideSMSToggle = false
      }
      this.isPhoneValid = true
    },
    isValidNumber (number) {
      if (this.isUSA) {
        return true
      }
      const num = number * 1
      this.isPhoneValid = number.length < 1 ? true : Number.isInteger(num) && number.length === 8
    },
    setNumber () {
      let num = ''
      // remove 45 or 44 from the start if not USA
      if (this.user.phone) {
        num = !this.isUSA && (this.user.phone.substr(0, 2) === this.activeCountry.countryCode) ? this.user.phone.replace(this.activeCountry.countryCode, '') : this.user.phone
      }
      this.initialPhone = num
      this.phone = num
    },
    updateUserSettings (contactType) {
      const userSettings = JSON.parse(this.userSettings)
      const varOption = 'dont_receive_' + contactType
      let settingsObj = {}
      settingsObj[varOption] = true
      const prop = Object.keys(settingsObj)[0]
      if (this.getContactOption(this.user, contactType) === 'deactivated') {
        delete userSettings[prop]
      } else {
        const val = settingsObj[prop]
        userSettings[prop] = val
      }
      this.userSettings = JSON.stringify(userSettings)
    },
    getContactOption (user, contactType) {
      const userSettings = JSON.parse(user.settings)
      const varOption = 'dont_receive_' + contactType
      if (userSettings[varOption]) {
        return 'deactivated'
      }
      if (contactType === 'phone' && !user.phone) {
        return 'no_number'
      }
      if (contactType === 'phone' && user.phone && user.unconfirmed_phone) {
        return 'awaiting_approval'
      }
      if (contactType === 'phone' && user.phone) {
        return 'active'
      }
      if (contactType === 'email' && !user.email) {
        return 'no_email'
      }
      if (contactType === 'email' && user.email) {
        return 'active'
      }
    },
    onEmailKeyup (val) {
      if (val.length === 0) {
        this.hideEmailToggle = true
      } else {
        this.hideEmailToggle = false
      }
      this.isEmailValid = true
    },
    submit () {
      let changes = {}
      if (this.isSMSActive) {
        this.isValidNumber(this.phone)
      }
      if (this.isEmailActive) {
        this.validateEmail(this.email)
      }
      if (!this.isEmailValid || !this.isPhoneValid) {
        return
      }
      if (this.isEmailActive && this.email !== this.initialEmail) {
        changes['email'] = this.email
      }
      if (this.isSMSActive && this.phone !== this.initialPhone) {
        if (this.phone.length > 0) {
          changes['phone'] = this.activeCountry.countryCode + this.phone
        } else {
          changes['phone'] = this.phone
        }
      }
      if (this.initialReceiveSMS !== this.receiveSMS) {
        this.updateUserSettings('phone')
        changes['settings'] = this.userSettings
      }
      if (this.initialReceiveEmails !== this.receiveEmails) {
        this.updateUserSettings('email')
        changes['settings'] = this.userSettings
      }
      docendo.updateUsers(window.SCHOOL.id, this.user.id, changes, success => {
        this.$store.commit('updateSchoolUser', success)
        eventbus.toast = {text: this.i18n[this.locale]['user_updated'], success: true}
        this.$emit('close')
      }, failure => {
        eventbus.toast = {text: this.$t('vue2.sms.an_error_occured'), success: false}
      })
    }
  },
  beforeMount () {
    if (window.SCHOOL.country) {
      const idxOfCountry = this.countries.findIndex(country => country.code === window.SCHOOL.country.toLowerCase())
      this.activeCountry = idxOfCountry > -1 ? this.countries[idxOfCountry] : this.countries[0]
    }
    this.userSettings = this.user.settings
    this.initialUserSettings = this.user.settings
    if (this.isSMSActive) {
      this.setNumber()
      const phoneSetting = this.getContactOption(this.user, 'phone')
      if (phoneSetting === 'no_number') {
        this.initialReceiveSMS = false
        this.receiveSMS = false
      } else {
        if (phoneSetting === 'deactivated' || phoneSetting === 'awaiting_approval') {
          this.initialReceiveSMS = false
          this.receiveSMS = false
        }
      }
    }
    if (this.isEmailActive) {
      this.email = this.user.email ? this.user.email : ''
      this.initialEmail = this.user.email ? this.user.email : ''
      if (!this.user.email) {
        this.receiveEmails = false
        this.initialReceiveEmails = false
      } else {
        if (this.getContactOption(this.user, 'email') === 'deactivated') {
          this.receiveEmails = false
          this.initialReceiveEmails = false
        }
      }
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.d-modal__user__settings__notifications {
  width: 450px;
}

.d-label {
  font-size: 1rem;
}

.d-modal__content h2 {
  margin-bottom: 1rem;
}

.d-input-group__toggle {
  display: inline-block;
  cursor: pointer;
}

.d-input-group.d-input-group__submit {
  margin: 2rem 0 0 0;
  display: flex;
  justify-content: flex-end;
}

.d-input-group.d-input-group__submit button {
  margin-left: 0.5rem;
}

.d-input-group {
  margin-bottom: 1rem;
}

.d-label {
  margin-bottom: 0.5rem;
}

.d-input-group__group {
  margin-top: 1rem;
  border: 1px solid var(--border-color);
  padding: 1rem;
  border-radius: 4px;
}

.d-input-group__group h3 {
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

.d-input-group__group h3 span {
  margin-right: 0.5rem;
}

.flag__input-lookalike {
  min-width: 75px;
  background-color: #ffffff;
  border-radius: 4px 0 0 4px;
  display: flex;
  align-items: center;
  justify-content: start;
  padding: 4px;
  border: 1px solid #edf2f7;
  border-width: 1px 0 1px 1px;
}

.flex-group {
  display: flex;
  box-shadow: 0 2px 5px -1px rgb(50 50 93 / 25%), 0 1px 3px -1px rgb(0 0 0 / 30%);
  border-radius: 4px;
}

.flex-group > input {
  box-shadow: none;
  border-radius: 0 4px 4px 0;
}

.flag__input-lookalike > span {
  display: flex;
  align-items: center;
}

.flag__input-lookalike > span svg {
  width: 18px;
  height: 12px;
}

.country-select__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 150px;
}

.country-select__item__name {
  display: flex;
  align-items: center;
}

img.country-flag {
  width: 20px;
  margin-right: 5px;
}

.d-label.d-pill {
  border: 1px solid #e9d4d5;
  padding: 4px 8px;
  border-radius: 4px;
  background: rgb(252, 237, 237);
  background: linear-gradient(90deg, rgba(252, 237, 237, 1) 0%, rgba(255, 255, 255, 1) 20%);
  font-weight: bold;
  font-size: 11px;
  display: flex;
  align-items: center;
}

.d-label.d-pill svg {
  width: 1.5rem;
  margin-right: 0.5rem;
  stroke: #ee7157;
}
</style>



// WEBPACK FOOTER //
// SendMessagesUserSetting.vue?d64ede72